import { useState } from 'react';

import { cn } from '@/nucleo/utilitarios';
import { formatarHsl, formatarRazao, lerHsl, razao } from '@/ui/estilos/contraste';
import { lerBloco, type Hsl } from '@/ui/estilos/lerTokens';
import tokensCss from '@/ui/estilos/tokens.css?raw';

/**
 * Valor inicial: o texto de ligação sobre o cartão do tema claro, lidos de
 * tokens.css — nada de cor escrita à mão como ponto de partida.
 */
const CLARO = lerBloco(tokensCss, ':root');
const FRENTE_INICIAL = formatarHsl(CLARO.get('link')!);
const FUNDO_INICIAL = formatarHsl(CLARO.get('card')!);

function Selo({ passou, rotulo }: { passou: boolean; rotulo: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        passou
          ? 'bg-card text-foreground border-success'
          : 'bg-card text-destructive-text border-destructive',
      )}
    >
      {rotulo}: {passou ? 'passa' : 'não passa'}
    </span>
  );
}

function CampoDeCor({
  id,
  rotulo,
  valor,
  cor,
  aoMudar,
}: {
  id: string;
  rotulo: string;
  valor: string;
  cor: Hsl | null;
  aoMudar: (v: string) => void;
}) {
  const idDoErro = `${id}-erro`;
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {rotulo}
      </label>
      <div className="flex items-center gap-2">
        <div
          className="h-8 w-8 rounded-lg border border-border shrink-0"
          aria-hidden="true"
          style={cor ? { backgroundColor: `hsl(${formatarHsl(cor)})` } : undefined}
        />
        <input
          id={id}
          type="text"
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          aria-invalid={cor === null}
          aria-describedby={cor === null ? idDoErro : undefined}
          className="flex h-9 w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm font-mono ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      {cor === null && (
        <p id={idDoErro} className="text-xs text-destructive-text">
          Escreva a cor como em tokens.css, por exemplo {FRENTE_INICIAL}.
        </p>
      )}
    </div>
  );
}

/**
 * Verificador de contraste para um par qualquer. A conta é `razao()` de
 * `ui/estilos/contraste.ts`, a mesma do auditor do build — não uma cópia.
 */
export function AccessibilityChecker() {
  const [frente, setFrente] = useState(FRENTE_INICIAL);
  const [fundo, setFundo] = useState(FUNDO_INICIAL);

  const corDaFrente = lerHsl(frente);
  const corDoFundo = lerHsl(fundo);
  const r = corDaFrente && corDoFundo ? razao(corDaFrente, corDoFundo) : null;

  return (
    <div
      className="rounded-xl border border-border bg-card p-5 space-y-4"
      data-search-id="accessibility-checker"
    >
      <p className="text-sm text-muted-foreground">
        Começa com <code>--link</code> sobre <code>--card</code> do tema claro. Troque por qualquer
        cor HSL para medir um par que a auditoria não confere.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CampoDeCor
          id="a11y-checker-fg"
          rotulo="Cor do texto (HSL)"
          valor={frente}
          cor={corDaFrente}
          aoMudar={setFrente}
        />
        <CampoDeCor
          id="a11y-checker-bg"
          rotulo="Cor de fundo (HSL)"
          valor={fundo}
          cor={corDoFundo}
          aoMudar={setFundo}
        />
      </div>

      {corDaFrente && corDoFundo && r !== null ? (
        <>
          <div
            className="rounded-xl border border-border p-6 text-center"
            data-print-keep-bg=""
            style={{ backgroundColor: `hsl(${formatarHsl(corDoFundo)})` }}
          >
            <p
              className="text-lg font-semibold"
              style={{ color: `hsl(${formatarHsl(corDaFrente)})` }}
            >
              Exemplo de texto — Transporte Escolar Gratuito
            </p>
            <p className="text-sm mt-1" style={{ color: `hsl(${formatarHsl(corDaFrente)})` }}>
              Texto auxiliar para verificar a leitura em tamanho menor
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2" aria-live="polite">
            <span className="text-sm font-mono font-bold text-foreground">
              Razão: {formatarRazao(r)}
            </span>
            <Selo passou={r >= 3} rotulo="Texto grande e interface (3:1)" />
            <Selo passou={r >= 4.5} rotulo="AA (4,5:1)" />
            <Selo passou={r >= 7} rotulo="AAA (7:1)" />
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          A razão aparece quando as duas cores forem válidas.
        </p>
      )}
    </div>
  );
}
